import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';
import backend from 'utils/backend';

import {Box, ButtonBase, Stack, TextField, Typography} from 'views/components';
import {Translate, translate} from 'views/module';
import {AssigneeBadge} from '../../../components';

const PlaybookEngagementPrompt = (props) => {
  const {addRule, rulePrompt, setRulePrompt} = props;

  const [assignee, setAssignee] = React.useState(null);
  const [admins, setAdmins] = React.useState([]);
  const [adminsLoading, setAdminsLoading] = React.useState(false);
  const [listVisible, setListVisible] = React.useState(false);
  const [commandQuery, setCommandQuery] = React.useState('');
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);
  const [trackedCommandStart, setTrackedCommandStart] = React.useState(null);

  const skipCommandInputRef = React.useRef(false);

  const closeCommandMenu = React.useCallback(() => {
    setListVisible(false);
    setCommandQuery('');
    setTrackedCommandStart(null);
    setHighlightedOptionIndex(0);
  }, []);

  const filteredAdmins = admins.filter((admin) => {
    const query = commandQuery.toLowerCase();
    const adminName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim().toLowerCase();
    const adminRn = (admin?.userRn || '').toLowerCase();

    return adminName.includes(query) || adminRn.includes(query);
  });

  const insertAssignee = React.useCallback((admin) => {
    if (trackedCommandStart === null) {
      return;
    }

    const displayName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || admin?.userRn || '';
    const currentMentionMatch = /@[^@\s]*$/.exec(rulePrompt.slice(trackedCommandStart));
    const mentionLength = currentMentionMatch?.[0]?.length || 0;
    const nextValue = `${rulePrompt.slice(0, trackedCommandStart)}@${displayName}${rulePrompt.slice(trackedCommandStart + mentionLength)}`;

    skipCommandInputRef.current = true;
    setRulePrompt(nextValue);
    setAssignee(admin);
    closeCommandMenu();
  }, [closeCommandMenu, rulePrompt, setRulePrompt, trackedCommandStart]);

  const submitRule = React.useCallback(() => {
    const trimmedPrompt = rulePrompt.trim();

    if (!trimmedPrompt) {
      return;
    }

    addRule({
      prompt: trimmedPrompt,
      ownerRns: assignee?.userRn ? [assignee.userRn] : [],
    });
    setRulePrompt('');
    setAssignee(null);
    closeCommandMenu();
  }, [addRule, assignee, closeCommandMenu, rulePrompt, setRulePrompt]);

  const handleCommandKeyDown = React.useCallback((event) => {
    if (listVisible) {
      if (event.key === 'Escape') {
        skipCommandInputRef.current = true;
        event.preventDefault();
        closeCommandMenu();
        return;
      }

      if (filteredAdmins.length > 0) {
        if (event.key === 'ArrowDown') {
          skipCommandInputRef.current = true;
          event.preventDefault();
          setHighlightedOptionIndex((prev) => (prev + 1) % filteredAdmins.length);
          return;
        }

        if (event.key === 'ArrowUp') {
          skipCommandInputRef.current = true;
          event.preventDefault();
          setHighlightedOptionIndex((prev) => (prev - 1 + filteredAdmins.length) % filteredAdmins.length);
          return;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          const selectedOption = filteredAdmins[highlightedOptionIndex] || filteredAdmins[0];

          if (selectedOption) {
            skipCommandInputRef.current = true;
            event.preventDefault();
            insertAssignee(selectedOption);
          }
          return;
        }
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitRule();
    }
  }, [closeCommandMenu, filteredAdmins, highlightedOptionIndex, insertAssignee, listVisible, submitRule]);

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [commandQuery]);

  React.useEffect(() => {
    if (assignee) {
      const assigneeDisplayName = `${assignee?.firstName || ''} ${assignee?.lastName || ''}`.trim() || assignee?.userRn || '';

      if (!assigneeDisplayName || !rulePrompt.includes(`@${assigneeDisplayName}`)) {
        setAssignee(null);
      }
    }
  }, [assignee, rulePrompt]);

  React.useEffect(() => {
    if (skipCommandInputRef.current) {
      skipCommandInputRef.current = false;
      return;
    }

    const mentionMatch = /(^|\s)@([^\s@]*)$/.exec(rulePrompt);

    if (!mentionMatch) {
      closeCommandMenu();
      return;
    }

    const commandStart = mentionMatch.index + mentionMatch[1].length;

    setTrackedCommandStart(commandStart);
    setListVisible(true);
    setCommandQuery((mentionMatch[2] || '').trimStart().toLowerCase());
  }, [closeCommandMenu, rulePrompt]);

  React.useEffect(() => {
    if (!listVisible) {
      return;
    }

    let mounted = true;
    const timeoutId = setTimeout(async () => {
      setAdminsLoading(true);
      try {
        const filter = [
          {key: 'status', value: ['ACTIVE']},
          {key: 'role', value: 'ADMIN'},
        ];
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.user.queryUser(filter, sortBy);
        const uniqueOptions = Array.from(
            new Map((result?.list ?? []).map((admin) => [admin.userRn, admin])).values(),
        );

        if (mounted) {
          setAdmins(uniqueOptions);
        }
      } catch (error) {
        console.error('Failed to load admins', error);
      } finally {
        if (mounted) {
          setAdminsLoading(false);
        }
      }
    }, 150);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [listVisible]);

  return (
    <Stack sx={{gap: '8px'}}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          overflow: 'visible',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#A8A49E',
            lineHeight: '100%',
            textTransform: 'uppercase',
          }}
          gutterBottom
        >
          <Translate expression='playbooks.New.RuleEditor.Title' />
        </Typography>
        <TextField
          value={rulePrompt}
          onChange={setRulePrompt}
          onKeyDown={handleCommandKeyDown}
          multiline
          minRows={2}
          onBlur={() => {
            window.setTimeout(() => {
              closeCommandMenu();
            }, 100);
          }}
          placeholder={translate('playbooks.New.RuleEditor.Placeholder')}
          sx={{
            'width': '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'transparent',
              position: 'relative',
              zIndex: 2,
              alignItems: 'flex-start',
              overflow: 'visible',
              p: 0,
            },
            '& .MuiOutlinedInput-input': {
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '150%',
              fontFamily: 'inherit',
              letterSpacing: 'inherit',
              px: '16px',
              py: '16px',
            },
            '& .MuiOutlinedInput-input.MuiInputBase-inputMultiline': {
              px: '16px',
              py: '16px',
              m: 0,
              overflow: 'visible !important',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              backgroundColor: 'transparent',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: '#A8A49E',
              opacity: 1,
            },
          }}
        />
        {
          listVisible &&
          <Box
            sx={{
              position: 'absolute',
              top: 'calc(100% + 1px)',
              left: 0,
              right: 0,
              maxHeight: '240px',
              overflowY: 'auto',
              border: '1px solid #E8E4DE',
              borderRadius: '12px',
              backgroundColor: '#FFFFFF',
              zIndex: 2147483647,
            }}
          >
            {
              adminsLoading &&
              <Typography
                sx={{
                  px: '12px',
                  py: '10px',
                  fontSize: '12px',
                  color: '#6B6760',
                }}
              >
                <Translate expression='playbooks.New.RuleEditor.Loading' />
              </Typography>
            }
            {
              !adminsLoading && filteredAdmins.length === 0 &&
              <Typography
                sx={{
                  px: '12px',
                  py: '10px',
                  fontSize: '12px',
                  color: '#6B6760',
                }}
              >
                <Translate expression='playbooks.New.RuleEditor.NoMatches' />
              </Typography>
            }
            {
              !adminsLoading &&
              filteredAdmins.map((admin, index) => {
                const displayName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || admin?.userRn;
                const isHighlighted = index === highlightedOptionIndex;

                return (
                  <ButtonBase
                    key={admin.userRn}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      insertAssignee(admin);
                    }}
                    sx={{
                      'width': '100%',
                      'justifyContent': 'flex-start',
                      'px': '12px',
                      'py': '10px',
                      'backgroundColor': isHighlighted ? '#F3F1EE' : '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#F8F6F3',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#1A1916',
                        lineHeight: '150%',
                      }}
                    >
                      @{displayName}
                    </Typography>
                  </ButtonBase>
                );
              })
            }
          </Box>
        }
      </Box>
      {
        assignee &&
        <Stack
          direction='row'
          alignItems='center'
          sx={{
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              color: '#6B6760',
              fontSize: '12px',
              lineHeight: '100%',
            }}
          >
            {translate('playbooks.New.RuleEditor.AssignedTo', {name: ''}).trim()}
          </Typography>
          <AssigneeBadge
            interactive={false}
            user={assignee}
            sx={{
              width: '24px',
              height: '24px',
              fontSize: '11px',
              outline: '2px solid',
              outlineColor: '#FFFFFF',
            }}
          />
        </Stack>
      }
    </Stack>
  );
};

PlaybookEngagementPrompt.propTypes = {
  addRule: PropTypes.func.isRequired,
  rulePrompt: PropTypes.string.isRequired,
  setRulePrompt: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, dispatch)(React.memo(PlaybookEngagementPrompt));
